/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import AccountBalance from '@mui/icons-material/AccountBalance';
import {
  Badge,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { cutText } from '../../helpers/text-parser';
import { NewBankAccountForm } from '../forms';
import GET_USER from '../../graphql/queries/get-user';
import DEFAULT_FILLED_INPUT_CLASS_NAME from '../../theme/constants/filled-input';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';
import DEFAULT_BADGE_CLASS_NAME from '../../theme/constants/badge';

const SelectBankAccount = ({
  bankId,
  setBankId,
  customCompany,
  hideText,
  onAccountCreate,
  customBankAccounts,
  refetchQueriesOnCreation,
  textFieldProps,
  typographyProps,
  currency,
}) => {
  const { data } = useQuery(GET_USER, { skip: customCompany });
  const [openForm, setOpenForm] = useState();
  const company = customCompany || data?.getUser.selectedCompany;
  const bankAccounts = (customBankAccounts
    || data?.getUser.selectedCompany?.bankAccounts)
    ?.filter((ba) => ba?.active)
    ?.filter((ba) => !currency || ba.currency.isoCode === currency);

  const handleAccountCreate = (newAccount) => {
    onAccountCreate(newAccount);
    setBankId(newAccount.id);
    setOpenForm(false);
  };

  const formatBankAccountText = (account) => `${
    account.accountType} ${account.bankName} en ${account.currency.isoCode} N°${account.accountNumber}`;

  return (
    <Grid container rowSpacing={1} columnSpacing={1} alignItems="center" justifyContent="center">
      {!hideText && (
        <Grid item xs={12}>
          <Typography {...typographyProps}>
            Seleccione la cuenta del banco para realizar el depósito:
          </Typography>
        </Grid>
      )}
      <Grid item xs={9}>
        <TextField
          select
          id="select-bank-account"
          variant="filled"
          value={bankId}
          onChange={({ target }) => setBankId(target.value)}
          disabled={!bankAccounts.length}
          inputProps={{
            sx: {
              paddingTop: '0px',
            },
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            className: DEFAULT_FILLED_INPUT_CLASS_NAME,
          }}
          SelectProps={{
            native: false,
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) {
                return <Typography sx={{ color: 'gray.A700', opacity: 0.5 }}>Selecciona una cuenta</Typography>;
              }
              const selectedAccount = bankAccounts.find((account) => account.id === selected);
              return selectedAccount ? cutText(formatBankAccountText(selectedAccount), 25, 0) : '';
            },
            sx: {
              border: '1px solid #ccc',
              backgroundColor: 'white',
              padding: 0,
              '&:hover': {
                backgroundColor: 'white',
              },
            },
          }}
          fullWidth
          {...textFieldProps}
        >
          {bankAccounts
            .filter((bank) => bank.active)
            .map((bank) => (
              <MenuItem key={bank.id} value={bank.id}>
                {formatBankAccountText(bank)}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid item xs={1}>
        <Tooltip title="Agregar cuenta">
          <IconButton
            value="add"
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setOpenForm(true);
            }}
          >
            <Badge
              badgeContent="+"
              className={DEFAULT_BADGE_CLASS_NAME}
              sx={{ '& .MuiBadge-badge': { fontSize: 25 } }}
            >
              <AccountBalance className={DEFAULT_ICON_CLASS_NAME} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Grid>
      { currency === 'USD' && (
        <Typography marginTop={2}>*Debe ser una cuenta en USD</Typography>
      )}
      {openForm && (
        <Drawer
          open={openForm}
          onClose={() => {
            setOpenForm(false);
          }}
          anchor="right"
          PaperProps={{
            sx: { width: { xs: '100%', md: '50%', lg: '25%' } },
          }}
          sx={{ zIndex: 1350, position: 'absolute' }}
        >
          <NewBankAccountForm
            cancel={() => {
              setOpenForm(false);
            }}
            accounts={bankAccounts}
            customCompany={company}
            onAccountCreate={handleAccountCreate}
            sx={{ padding: 2 }}
            refetchQueriesOnCreation={refetchQueriesOnCreation}
          />
        </Drawer>
      )}
    </Grid>
  );
};

SelectBankAccount.propTypes = {
  setBankId: PropTypes.func.isRequired,
  bankId: PropTypes.string,
  customCompany: PropTypes.shape({
    id: PropTypes.string,
    rut: PropTypes.number,
    name: PropTypes.string,
  }),
  customBankAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      bankName: PropTypes.string,
      accountNumber: PropTypes.string,
    }),
  ),
  hideText: PropTypes.bool,
  onAccountCreate: PropTypes.func,
  refetchQueriesOnCreation: PropTypes.array,
  textFieldProps: PropTypes.object,
  typographyProps: PropTypes.object,
  currency: PropTypes.string,
};

SelectBankAccount.defaultProps = {
  onAccountCreate: () => {},
  bankId: '',
  customBankAccounts: null,
  customCompany: null,
  hideText: false,
  refetchQueriesOnCreation: [GET_USER],
  textFieldProps: {},
  typographyProps: { variant: 'h2' },
  currency: null,
};

export default SelectBankAccount;
